/**
 * GraphQL Utility Hooks
 *
 * Hooks for utility queries like mention autocomplete.
 *
 * Feature: 065-graphql-migration-cleanup
 * Feature: 071-ai-critique-sessions (removed AI availability - moved to session-based)
 * Tasks: T048
 */

import { useQuery, gql } from '@apollo/client';
import { getVariantUrl } from '../../components/photos/AdaptivePhoto';
import { GET_CRITIQUE_SESSION } from '../queries/sessions';

// =============================================================================
// Mention Autocomplete (T048)
// =============================================================================

const COLLECTION_ITEMS_FOR_MENTION_QUERY = gql`
  query CollectionItemsForMention($collectionId: ID!, $limit: Int) {
    collectionItems(collectionId: $collectionId, limit: $limit, offset: 0) {
      items {
        id
        photoId
        photo {
          id
          globalId
          title
          variants {
            id
            variantType
            storageUrl
            publicUrl
            width
            height
            fileSize
          }
        }
      }
    }
  }
`;

interface CollectionItemsForMentionData {
  collectionItems: {
    items: Array<{
      id: string;
      photoId: string;
      photo: {
        id: string;
        globalId: string;
        title: string | null;
        variants?: Array<{
          id: string;
          variantType: string;
          storageUrl: string;
          publicUrl?: string | null;
          width: number;
          height: number;
          fileSize: number;
        }>;
      } | null;
    }>;
  };
}

export interface MentionAutocompleteOptionGraphQL {
  /** Global object ID (e.g., "ph1a") */
  objectId: string;

  /** Object type */
  type: 'photos' | 'users' | 'collections';

  /** Display label */
  label: string;

  /** Photo thumbnail URL (for photos) */
  thumbnailUrl?: string;
}

/**
 * Fetch mention autocomplete options from collection photos
 *
 * GraphQL replacement for useMentionAutocomplete (REST)
 *
 * @param collectionId - Collection context for photo suggestions
 * @param options - Configuration options
 * @returns Autocomplete state and options
 */
export function useMentionAutocompleteGraphQL(
  collectionId: string | undefined,
  options: {
    /** Maximum number of options to return */
    limit?: number;
    /** Enable/disable the hook */
    enabled?: boolean;
  } = {}
) {
  const { limit = 100, enabled = true } = options;

  const { data, loading, error } = useQuery<CollectionItemsForMentionData>(
    COLLECTION_ITEMS_FOR_MENTION_QUERY,
    {
      variables: { collectionId, limit },
      skip: !enabled || !collectionId,
      fetchPolicy: 'cache-and-network',
    }
  );

  // Convert collection items to autocomplete options
  const allOptions: MentionAutocompleteOptionGraphQL[] = [];

  if (data?.collectionItems?.items) {
    for (const item of data.collectionItems.items) {
      if (item.photo) {
        allOptions.push({
          objectId: item.photo.globalId || item.photoId,
          type: 'photos',
          label: item.photo.title || `Photo ${item.photo.globalId || item.photoId}`,
          thumbnailUrl: getVariantUrl({ id: item.photo.id, variants: item.photo.variants || [] } as any, 'thumbnail') || undefined,
        });
      }
    }
  }

  return {
    options: allOptions,
    isLoading: loading,
    error: error as Error | null,
    totalCount: allOptions.length,
  };
}

// =============================================================================
// Session Participants (Feature 071)
// =============================================================================

export interface SessionParticipant {
  /** User's global ID */
  id: string;
  /** Username for @-mention */
  username: string;
  /** Display name (may differ from username) */
  displayName?: string;
}

interface CritiqueSessionData {
  critiqueSession: {
    id: string;
    collection: {
      user: {
        id: string;
        username: string;
        displayName?: string;
      };
    };
    critiques: {
      items: Array<{
        id: string;
        user: {
          id: string;
          username: string;
          displayName?: string;
        };
      }>;
    };
  } | null;
}

/**
 * Fetch session participants for @-mention autocomplete
 *
 * Extracts unique users from session critiques plus the collection owner.
 * Feature 071: AI-Owner Conversation support
 *
 * @param sessionId - Session ID to fetch participants from
 * @param options - Configuration options
 * @returns Participants list and loading state
 */
export function useSessionParticipantsGraphQL(
  sessionId: string | undefined,
  options: {
    /** Enable/disable the hook */
    enabled?: boolean;
    /** Current user ID to exclude from suggestions */
    excludeUserId?: string;
  } = {}
) {
  const { enabled = true, excludeUserId } = options;

  const { data, loading, error } = useQuery<CritiqueSessionData>(
    GET_CRITIQUE_SESSION,
    {
      variables: { id: sessionId },
      skip: !enabled || !sessionId,
      fetchPolicy: 'cache-first', // Use cached session data when available
    }
  );

  // Extract unique participants from session (excluding current user)
  const participants: SessionParticipant[] = [];
  const seenUserIds = new Set<string>();

  // Always exclude the current user from suggestions
  if (excludeUserId) {
    seenUserIds.add(excludeUserId);
  }

  if (data?.critiqueSession) {
    const session = data.critiqueSession;

    // Add collection owner first
    if (session.collection?.user) {
      const owner = session.collection.user;
      if (!seenUserIds.has(owner.id)) {
        seenUserIds.add(owner.id);
        participants.push({
          id: owner.id,
          username: owner.username,
          displayName: owner.displayName,
        });
      }
    }

    // Add critique authors
    if (session.critiques?.items) {
      for (const critique of session.critiques.items) {
        if (critique.user && !seenUserIds.has(critique.user.id)) {
          seenUserIds.add(critique.user.id);
          participants.push({
            id: critique.user.id,
            username: critique.user.username,
            displayName: critique.user.displayName,
          });
        }
      }
    }
  }

  return {
    participants,
    isLoading: loading,
    error: error as Error | null,
  };
}
